// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { useGetMissingRequirementsForDate } from "@src/appV2/Accounts/Documents/api/useGetMissingRequirementsForDate";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  BookabilityDecision,
  BookabilityUnmetCriteria,
} from "@src/appV2/OpenShifts/ShiftAction/types";
import { getQualificationForDocumentCheck } from "@src/appV2/redesign/Documents/helpers";
import { WORKPLACE_DOCUMENTS_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { createBackgroundCheckUrlFromMissingDataTypes } from "@src/appV2/redesign/Shift/createBackgroundCheckUrlFromMissingDataTypes";
import { getBackgroundCheckContext } from "@src/appV2/redesign/Shift/getBackgroundCheckContext";
import { type UseModalState, useModalState } from "@src/appV2/redesign/system/ui-components";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useState } from "react";
import { generatePath, useHistory, useRouteMatch } from "react-router-dom";

import { Button } from "../../components/Button";
import { LoadingButton } from "../../components/LoadingButton";
import {
  type InstantBookPreferredReason,
  type ShiftBlockWithMappedDetails,
} from "./api/useFetchPaginatedShiftBlocks";
import { useFetchWorkerPreferredStatus } from "./api/useFetchPreferredWorker";
import { useShiftBlockBookability } from "./api/useShiftBlockBookability";
import { useUpdateShiftBlock } from "./api/useUpdateBlockShift";
import { BookShiftBlockBottomSheet } from "./BookShiftBlockBottomSheet";
import { MissingDocumentsBottomSheet } from "./MissingDocumentsBottomSheet";
import { NegotiationBlockButton } from "./NegotiateBlockButton";
import { RequestToBookShiftBlockButton } from "./RequestToBookShiftBlockButton";
import { BookShiftBlockErrors, SHIFT_BLOCK_ACTIONS } from "./utils";

interface Props {
  facilityId: string;
  agentId: string;
  shiftBlockId: string;
  shifts: ShiftBlockWithMappedDetails["shifts"];
  blockAgentRequirement: ShiftBlockWithMappedDetails["agentReq"];
  instantBookPreferredReasons: Array<
    (typeof InstantBookPreferredReason)[keyof typeof InstantBookPreferredReason]
  >;
  hourlyPayForShiftBlock: number;
  isBlockNegotiable: boolean;
  workplaceName: string;
  workerName: string;
  orientationBlockId?: string;
  bookShiftBlockModalState?: UseModalState;
  requestToBookShiftBlockModalState?: UseModalState;
  onBookingShiftBlock: () => void;
}

export function BookShiftBlockButton(props: Props) {
  const {
    facilityId,
    agentId,
    shiftBlockId,
    shifts,
    blockAgentRequirement,
    instantBookPreferredReasons,
    hourlyPayForShiftBlock,
    isBlockNegotiable,
    workplaceName,
    workerName,
    orientationBlockId,
    bookShiftBlockModalState: parentBookShiftBlockModalState,
    requestToBookShiftBlockModalState: parentRequestToBookShiftBlockModalState,
    onBookingShiftBlock,
  } = props;
  const { showErrorToast, showSuccessToast, showWarningToast } = useToast();
  const worker: Worker = useDefinedWorker();

  const workerPreferenceQualification = worker?.preference?.qualification;
  const internalBookShiftBlockState = useModalState();
  const bookShiftBlockState = parentBookShiftBlockModalState ?? internalBookShiftBlockState;
  const internalRequestToBookShiftBlockState = useModalState();
  const requestToBookShiftBlockState =
    parentRequestToBookShiftBlockModalState ?? internalRequestToBookShiftBlockState;
  const missingDocumentsDialogState = useModalState();
  const history = useHistory();
  const { path: currentRouterUrl } = useRouteMatch();
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false);
  const { mutateAsync: mutateBookShiftBlock, isLoading: isLoadingBookShiftBlock } =
    useUpdateShiftBlock({
      shiftBlockId,
    });
  const {
    data: shiftBookabilityData,
    isSuccess: isShiftBookabilitySuccess,
    isLoading: isShiftBookabilityLoading,
  } = useShiftBlockBookability(
    {
      facilityId,
      agentId,
      shiftIds: shifts.map((shift) => shift.id),
      returnFirstUnmetCriteria: false,
    },
    {
      enabled: shifts.length > 0,
    }
  );

  const {
    mutateAsync: getMissingRequirementsForDate,
    data: missingRequirementsForDate,
    isLoading: isGetMissingRequirementsLoading,
    isSuccess: isGetMissingRequirementsSuccess,
  } = useGetMissingRequirementsForDate();
  const isBookingInFlight = isBookingSubmitting || isLoadingBookShiftBlock;

  const shiftWithBlockedStatus = shiftBookabilityData?.data?.find(
    (item) => item.attributes.bookability.decision === BookabilityDecision.BLOCKED
  );
  const shiftBlockedDueToBackgroundCheck = shiftBookabilityData?.data?.find((item) => {
    const backgroundCheckContext = getBackgroundCheckContext(item);
    return backgroundCheckContext.isMissingData || backgroundCheckContext.isRejected;
  });

  const { data: workerPreferredStatus, isInitialLoading: isWorkerPreferredStatusLoading } =
    useFetchWorkerPreferredStatus(
      {
        workplaceId: facilityId,
        workerId: agentId,
      },
      {
        enabled: isShiftBookabilitySuccess && !isDefined(shiftWithBlockedStatus),
      }
    );

  const handleBackgroundCheckClicked = useCallback(() => {
    const backgroundCheckContext = getBackgroundCheckContext(shiftBlockedDueToBackgroundCheck!);

    if (backgroundCheckContext.isRejected) {
      showErrorToast("Your national background check has not been processed and approved yet.");
      return;
    }

    history.push(
      createBackgroundCheckUrlFromMissingDataTypes({
        queryParams: new URLSearchParams(history.location.search),
        shiftId: shiftBlockId,
        missingBackgroundCheckDataTypes: backgroundCheckContext.missingDataTypes,
      })
    );
  }, [history, shiftBlockId, shiftBlockedDueToBackgroundCheck, showErrorToast]);

  const handleUploadRequiredDocumentsClicked = useCallback(async () => {
    // eslint-disable-next-line unicorn/prefer-at
    const lastShift = shifts[shifts.length - 1];
    try {
      if (isDefined(lastShift)) {
        const missingDocumentsForDate = await getMissingRequirementsForDate({
          hcfId: facilityId,
          date: lastShift.end,
          qualification: getQualificationForDocumentCheck({
            shiftQualificationRequirement: blockAgentRequirement ?? "",
            selectedWorkerQualification: workerPreferenceQualification ?? "",
          }),
        });
        if (missingDocumentsForDate?.data.hcpMissingRequirementsForDate.length > 0) {
          missingDocumentsDialogState.openModal();
        } else {
          showWarningToast("You have no missing documents for this block.");
        }

        logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
          action: SHIFT_BLOCK_ACTIONS.UPLOAD_REQUIRED_DOCUMENTS_TAPPED,
          shiftBlockId,
          facilityId,
          location: currentRouterUrl,
          missingDocuments: missingDocumentsForDate,
        });
      }
    } catch (error) {
      const { message: errorMessage = "Unknown error" } = error instanceof Error ? error : {};
      logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
        action: SHIFT_BLOCK_ACTIONS.UPLOAD_REQUIRED_DOCUMENTS_TAPPED,
        shiftBlockId,
        facilityId,
        location: currentRouterUrl,
        errorMessage,
      });
      showErrorToast("Something went wrong while checking your documents. Please try again.");
    }
  }, [
    blockAgentRequirement,
    currentRouterUrl,
    facilityId,
    getMissingRequirementsForDate,
    missingDocumentsDialogState,
    shiftBlockId,
    shifts,
    showErrorToast,
    showWarningToast,
    workerPreferenceQualification,
  ]);

  const handleConfirmMissingDocuments = useCallback(() => {
    const qualification = getQualificationForDocumentCheck({
      shiftQualificationRequirement: blockAgentRequirement ?? "",
      selectedWorkerQualification: workerPreferenceQualification ?? "",
    });

    const searchParams = new URLSearchParams();
    if (qualification.length > 0) {
      searchParams.set("qualification", qualification);
    }

    const workplaceDocumentsPath = generatePath(WORKPLACE_DOCUMENTS_FULL_PATH, {
      workplaceId: facilityId,
    });
    const queryString = searchParams.toString();
    history.push(queryString ? `${workplaceDocumentsPath}?${queryString}` : workplaceDocumentsPath);
  }, [blockAgentRequirement, facilityId, history, workerPreferenceQualification]);

  const handleBookBlockClicked = useCallback(() => {
    if (isBookingInFlight) {
      return;
    }

    if (
      isDefined(shiftWithBlockedStatus) &&
      shiftWithBlockedStatus.attributes.bookability.decision === BookabilityDecision.BLOCKED
    ) {
      logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
        action: SHIFT_BLOCK_ACTIONS.BOOK_BLOCK_TAPPED,
        shiftBlockId,
        facilityId,
        unmetCriteria: shiftWithBlockedStatus.attributes.bookability.unmetCriteria,
        location: currentRouterUrl,
      });
      // There can be multiple reasons for the shift block booking being blocked, however for the purposes of showing the error message to the user, we are only showing the first unmet criteria.
      showErrorToast(
        BookShiftBlockErrors[shiftWithBlockedStatus.attributes.bookability.unmetCriteria[0]] ??
          "Error: You cannot book this block."
      );
    } else {
      logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
        action: SHIFT_BLOCK_ACTIONS.BOOK_BLOCK_TAPPED,
        shiftBlockId,
        facilityId,
        location: currentRouterUrl,
      });
      bookShiftBlockState.openModal();
    }
  }, [
    bookShiftBlockState,
    currentRouterUrl,
    facilityId,
    isBookingInFlight,
    shiftBlockId,
    shiftWithBlockedStatus,
    showErrorToast,
  ]);

  const handleConfirmBooking = useCallback(async () => {
    if (isBookingInFlight) {
      return;
    }

    setIsBookingSubmitting(true);

    try {
      const offers = shifts
        .filter((shift) => shift.offer)
        .map((shift) => ({ id: shift.offer?.id ?? "", shiftId: shift.id }));
      await mutateBookShiftBlock({
        data: {
          type: "shift-block",
          id: shiftBlockId,
          attributes: {
            agentId,
            offers,
          },
        },
      });
      showSuccessToast("Shift block booked successfully.");
      onBookingShiftBlock();
    } catch {
      showErrorToast("Something went wrong while booking the shift block.");
    } finally {
      setIsBookingSubmitting(false);
    }
  }, [
    agentId,
    isBookingInFlight,
    mutateBookShiftBlock,
    onBookingShiftBlock,
    shiftBlockId,
    shifts,
    showErrorToast,
    showSuccessToast,
  ]);

  if (isShiftBookabilityLoading || isWorkerPreferredStatusLoading) {
    return null;
  }

  const missingDocuments = isGetMissingRequirementsSuccess
    ? missingRequirementsForDate.data.hcpMissingRequirementsForDate.map((params) => ({
        reqId: params.reqId,
        name: params.name,
        visibleToHCP: params.visibleToHCP,
      }))
    : [];

  const missingDocumentsSet = new Set(missingDocuments);

  if (isDefined(shiftBlockedDueToBackgroundCheck)) {
    return (
      <Button
        fullWidth
        variant="contained"
        size="medium"
        disabled={!isShiftBookabilitySuccess}
        onClick={handleBackgroundCheckClicked}
      >
        Book This Block
      </Button>
    );
  }

  if (
    isDefined(shiftWithBlockedStatus) &&
    shiftWithBlockedStatus.attributes.bookability.decision === BookabilityDecision.BLOCKED &&
    shiftWithBlockedStatus.attributes.bookability.unmetCriteria.includes(
      BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS
    )
  ) {
    return (
      <>
        <LoadingButton
          fullWidth
          isLoading={isGetMissingRequirementsLoading}
          variant="contained"
          size="medium"
          onClick={handleUploadRequiredDocumentsClicked}
        >
          Upload Required Documents
        </LoadingButton>
        <MissingDocumentsBottomSheet
          modalState={missingDocumentsDialogState}
          missingDocuments={missingDocumentsSet}
          analyticsProps={{
            shiftBlockId,
            facilityId,
            location: currentRouterUrl,
          }}
          onConfirm={handleConfirmMissingDocuments}
        />
      </>
    );
  }

  const isWorkerEligibleForInstantBooking =
    instantBookPreferredReasons.length === 0 ||
    (workerPreferredStatus?.preferred ?? []).some((preferredWorker) =>
      instantBookPreferredReasons.includes(preferredWorker.reason)
    );

  if (isWorkerEligibleForInstantBooking) {
    return (
      <>
        <LoadingButton
          fullWidth
          variant="contained"
          size="medium"
          disabled={!isShiftBookabilitySuccess || isBookingInFlight}
          isLoading={isBookingInFlight}
          onClick={handleBookBlockClicked}
        >
          Book This Block
        </LoadingButton>
        {isBlockNegotiable && (
          <NegotiationBlockButton
            isDisabled={!isShiftBookabilitySuccess}
            hourlyPayForShiftBlock={hourlyPayForShiftBlock}
            shiftBlockId={shiftBlockId}
            workplaceId={facilityId}
            workerId={agentId}
            workplaceName={workplaceName}
            workerName={workerName}
          />
        )}
        <BookShiftBlockBottomSheet
          modalState={bookShiftBlockState}
          shifts={shifts}
          shiftBlockId={shiftBlockId}
          facilityId={facilityId}
          orientationBlockId={orientationBlockId}
          onConfirmBooking={() => {
            void handleConfirmBooking();
          }}
        />
      </>
    );
  }

  return (
    <>
      <RequestToBookShiftBlockButton
        disabled={!isShiftBookabilitySuccess}
        shiftWithBlockedStatus={shiftWithBlockedStatus}
        facilityId={facilityId}
        agentId={agentId}
        shiftBlockId={shiftBlockId}
        shifts={shifts}
        modalState={requestToBookShiftBlockState}
      />
      {isBlockNegotiable && (
        <NegotiationBlockButton
          isDisabled={!isShiftBookabilitySuccess}
          hourlyPayForShiftBlock={hourlyPayForShiftBlock}
          shiftBlockId={shiftBlockId}
          workplaceId={facilityId}
          workerId={agentId}
          workplaceName={workplaceName}
          workerName={workerName}
        />
      )}
    </>
  );
}
